/* eslint-disable @typescript-eslint/camelcase */
import {
  Grid,
  IconButton,
  makeStyles,
  Tooltip,
  Divider,
} from '@material-ui/core';
import InputIcon from '@material-ui/icons/Input';
import WarningOutlinedIcon from '@material-ui/icons/WarningOutlined';
import clsx from 'clsx';
import ButtonExport from 'components/ButtonExport';
import ClickTypography from 'components/ClickTypography';
import PageTitle from 'components/PageTitle';
import SearchFilterBar from 'components/SearchFilterBar';
import TableItems, {
  MenuActionItem,
  MenuActions,
  TableCell,
  TableRow,
} from 'components/TableItems';
import Permission from 'components/ValidPermission';
import { useTableFilters } from 'hooks/useTableFilters';
import i18n from 'i18n';
import { selectCompany, selectPartner } from 'modules/authenticate/actions';
import {
  CompanyData,
  PartnerData,
  PermissionsData,
} from 'modules/authenticate/interfaces';
import {
  exportClientSummaryRequest,
  getAllClientsOperatorRequest,
} from 'modules/clients/actions';
import {
  AllClientsFields,
  ListAllClientsDataProps,
} from 'modules/clients/interfaces';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import ApplicationState from 'store/interfaces';
import { formatDateValue } from 'utils/dateFormat';
import { getCompanyName } from 'utils/tsHelpers';
import ClientsDashboardCards from './ClientsDashboardCards';
import { filterOptions } from './filterOptions';
import FormCloneClient from './FormCloneClient';
import styles from './styles';
import { headers } from './tableHeaders';

const useStyles = makeStyles(styles);

interface Props {
  clientsList: ListAllClientsDataProps;
  isLoading: boolean;
  permissions?: PermissionsData;
  exportClientSummaryRequest: (filtersDashboard) => void;
  getAllClientsOperatorRequest: (props) => void;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
  selectCompany: (company: CompanyData | null) => void;
  selectPartner: (partner: PartnerData | null) => void;
}

const AllClientsList = ({
  clientsList,
  isLoading,
  permissions,
  exportClientSummaryRequest,
  getAllClientsOperatorRequest,
  modalClose,
  modalOpen,
  selectCompany,
  selectPartner,
}: Props) => {
  const classes = useStyles();

  const {
    itemOrder,
    query,
    searchValue,
    setQuery,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    setSearchValue,
  } = useTableFilters();

  const [showTable, setShowTable] = React.useState(false);
  const [refresh, setRefresh] = React.useState(false);
  const history = useHistory();

  const getClients = React.useCallback(
    (refresh = false) => {
      if (!showTable) return;

      getAllClientsOperatorRequest({
        ...query,
      });

      setRefresh(refresh);
    },
    [getAllClientsOperatorRequest, query, showTable]
  );

  React.useEffect(() => {
    getClients();
  }, [getClients]);

  const handleClick = (item: AllClientsFields, toUrl: string) => {
    selectCompany({
      id: item?.company_uuid,
      name: getCompanyName(item),
      shortName: getCompanyName(item),
    });
    selectPartner({
      name: item?.partner_name,
      shortName: item?.partner_name,
      id: item?.partner_uuid,
    });
    history.push(toUrl);
  };

  const renderIconMaintenance = item => {
    if (item) {
      return (
        <Tooltip
          placement="bottom"
          arrow
          title={i18n.t('CLIENT_MAINTENANCE_WARNING_KEY')}
        >
          <WarningOutlinedIcon color="error" />
        </Tooltip>
      );
    }
    return <></>;
  };

  const handleToContext = item =>
    handleClick(item, `/clients/${item?.client_uuid}/configurations`);

  const getHeadersPDF = () => [
    headers
      .filter(header => header.key !== '')
      .map(header => i18n.t(header.i18n)),
  ];

  const getClientsListPDF: Array<[
    string,
    string,
    string,
    number,
    string
  ]> = clientsList?.results.map(item => [
    item?.client_name,
    item?.environment_name,
    item?.partner_name,
    item?.num_users,
    String(formatDateValue(item?.user_last_access)),
  ]);

  const getCSV = () => {
    exportClientSummaryRequest({
      query,
    });
  };

  return (
    <Grid container>
      <Grid
        container
        direction="row"
        alignItems="center"
        justify="space-between"
      >
        <PageTitle
          title={i18n.t('CLIENTS_PAGE.LIST_CLIENTS_TITLE_KEY')}
          subtitle={i18n.t('AVAILABLE_CLIENTS_LIST_KEY')}
        />

        {!!permissions && permissions?.can_export_client_summary_list && (
          <ButtonExport
            context={i18n.t('CLIENTS_KEY')}
            getHeadersPDF={getHeadersPDF()}
            getBodyListPDF={getClientsListPDF}
            csvAction={getCSV}
            filters={query.filters}
            filterOptions={filterOptions}
          />
        )}
      </Grid>

      <ClientsDashboardCards
        filters={query.filters}
        onApplyFilter={() => setShowTable(true)}
        setQuery={setQuery}
      />

      <Divider style={{ width: '100%', marginTop: '16px' }} />

      <Grid container className={classes.searchContainer}>
        <SearchFilterBar
          filterOptions={filterOptions}
          isLoading={isLoading}
          searchValue={searchValue}
          query={query}
          onApplyFilters={() => setShowTable(true)}
          setSearchValue={setSearchValue}
          setQuery={setQuery}
          filteredItemsCounterProps={{
            countFilteredItems: clientsList?.count,
            countTotalItems: clientsList?.total_count,
            isLoading,
            totalItemsText: i18n.t('CLIENTS_TOTAL_KEY'),
          }}
        />
        {showTable && (
          <TableItems
            header={
              <>
                {headers.map(item => (
                  <TableCell
                    centerTitle={item?.centerTitle}
                    textAlignCenter
                    key={item?.key}
                    markOderItem={itemOrder === item?.key}
                    style={item?.style || {}}
                    order={item?.order}
                    handleOrder={() =>
                      item?.order ? handleOrder(item?.key) : null
                    }
                  >
                    {i18n.t(item?.i18n)}
                  </TableCell>
                ))}
              </>
            }
            renderItems={(item: AllClientsFields, index) => (
              <TableRow
                key={item?.client_uuid}
                className={clsx(classes.summaryRowTable, {
                  [classes.envRow]: !!item?.in_maintenance,
                })}
              >
                <TableCell
                  maxLength={10}
                  ellipsis
                  centerTitle
                  className={classes.centerFields}
                  onClick={() => handleToContext(item)}
                >
                  <IconButton
                    id={`access-client-${index}`}
                    onClick={() => handleToContext(item)}
                  >
                    <InputIcon className={classes.accessButton} />
                  </IconButton>
                </TableCell>
                <TableCell
                  tooltip={item?.client_name}
                  className={`${classes.longField} ${classes.longNameField}`}
                  onClick={() => handleToContext(item)}
                >
                  <Grid className={classes.textAndIcon}>
                    <ClickTypography
                      item={item}
                      value={item?.client_name}
                      classes={classes}
                      handleClick={handleClick}
                    />
                    {item?.in_maintenance &&
                      renderIconMaintenance(item?.in_maintenance)}
                  </Grid>
                </TableCell>
                <TableCell
                  tooltip={item?.environment_name}
                  className={`${classes.longField} ${classes.longNameField}`}
                  onClick={() => handleToContext(item)}
                >
                  <ClickTypography
                    item={item}
                    value={item?.environment_name}
                    classes={classes}
                    handleClick={handleClick}
                  />
                </TableCell>
                <TableCell
                  tooltip={item?.partner_name}
                  className={classes.longField}
                  onClick={() => handleToContext(item)}
                >
                  <ClickTypography
                    item={item}
                    value={item?.partner_name}
                    classes={classes}
                    handleClick={handleClick}
                  />
                </TableCell>
                <TableCell
                  ellipsis
                  copyable
                  className={classes.numberField}
                  onClick={() => handleToContext(item)}
                >
                  <ClickTypography
                    item={item}
                    value={item?.num_users}
                    classes={classes}
                    handleClick={handleClick}
                  />
                </TableCell>
                <TableCell
                  centerTitle
                  textAlignCenter
                  className={classes.dateField}
                  onClick={() => handleToContext(item)}
                >
                  {formatDateValue(item?.user_last_access)}
                </TableCell>
                <TableCell style={{ textAlign: 'center' }}>
                  <MenuActions id={`btn-options-clients-${index}`}>
                    <Permission value="clone_client">
                      <MenuActionItem
                        id="btn-clone-client"
                        onClick={() =>
                          modalOpen({
                            title: i18n.t('CLIENTS_PAGE.CLONE_CLIENT_KEY'),
                            content: (
                              <FormCloneClient
                                clientUuid={item?.client_uuid}
                                clientCopyName={item?.client_name}
                                onCloseModal={modalClose}
                                onCancel={modalClose}
                                clientData={item}
                              />
                            ),
                            maxWidth: 'sm',
                            noBorder: true,
                          })
                        }
                      >
                        {i18n.t('CLONE_KEY')}
                      </MenuActionItem>
                    </Permission>
                    <Permission value="edit_client">
                      <MenuActionItem
                        id="btn-edit-client"
                        onClick={() => {
                          handleClick(
                            item,
                            `/clients/${item?.client_uuid}/configurations`
                          );
                        }}
                      >
                        {i18n.t('EDIT_KEY')}
                      </MenuActionItem>
                    </Permission>
                    <Permission value="list_users">
                      <MenuActionItem
                        id="btn-users-client"
                        component={Link}
                        to={`/clients/${item?.client_uuid}/users`}
                      >
                        {i18n.t('USERS_KEY')}
                      </MenuActionItem>
                    </Permission>
                    <Permission value="list_available_applications">
                      <MenuActionItem
                        id="btn-applications-client"
                        component={Link}
                        to={`/clients/${item?.client_uuid}/applications`}
                      >
                        {i18n.t('APPLICATIONS_KEY')}
                      </MenuActionItem>
                    </Permission>
                    <Permission
                      value={[
                        'list_environment_instance_sessions',
                        'list_client_instance_sessions',
                        'list_session_history',
                      ]}
                    >
                      <MenuActionItem
                        id="btn-sessions-client"
                        component={Link}
                        to={`/clients/${item?.client_uuid}/sessions`}
                      >
                        {i18n.t('SESSIONS_KEY')}
                      </MenuActionItem>
                    </Permission>
                    <Permission value="client_access_control">
                      <MenuActionItem
                        id="btn-restrictions-client"
                        component={Link}
                        to={`/clients/${item?.client_uuid}/access-restrictions`}
                      >
                        {i18n.t('RESTRICTIONS_KEY')}
                      </MenuActionItem>
                    </Permission>
                    <Permission value="get_client_maintenance">
                      <MenuActionItem
                        id="btn-maintenance-client"
                        component={Link}
                        to={`/clients/${item?.client_uuid}/maintenance`}
                      >
                        {i18n.t('MAINTENANCE_KEY')}
                      </MenuActionItem>
                    </Permission>
                    <Permission value="list_client_logs">
                      <MenuActionItem
                        id="btn-logs-client"
                        component={Link}
                        to={`/clients/${item?.client_uuid}/logs`}
                      >
                        {i18n.t('LOGS_KEY')}
                      </MenuActionItem>
                    </Permission>
                  </MenuActions>
                </TableCell>
              </TableRow>
            )}
            data={clientsList}
            isLoading={!refresh && isLoading}
            onChangePage={handlePageChange}
            onChangeLimit={handleLimitChange}
            notFoundText={i18n.t('CLIENTS_PAGE.NO_CLIENTS_KEY')}
          />
        )}
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  clients: {
    isLoading,
    clientsList,
    isLoadingBlockMultipleSessionReport,
    isLoadingConnectionTypeReport,
    isLoadingMFAReport,
    isLoadingUseProfileReport,
    isLoadingVirtualizerReport,
  },
}: ApplicationState) => ({
  isLoading:
    isLoading ||
    isLoadingBlockMultipleSessionReport ||
    isLoadingConnectionTypeReport ||
    isLoadingMFAReport ||
    isLoadingUseProfileReport ||
    isLoadingVirtualizerReport,
  clientsList,
  permissions: data?.permissions,
});

export default connect(mapStateToProps, {
  selectCompany,
  selectPartner,
  getAllClientsOperatorRequest,
  modalOpen,
  modalClose,
  exportClientSummaryRequest,
})(AllClientsList);
